import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { ItemDetailContentData } from '@/experiences/sales-portal/domain/items/blocs/types/item-detail-content';
import { ItemDetailSection } from '@/experiences/sales-portal/domain/items/components/item-detail-section';
import { ProductDescription } from '@/experiences/sales-portal/domain/items/components/details-tab/product-description';
import { ScrollView } from 'react-native';
import { VariantPackList } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/item-detail-content/details-tab/variant-pack/variant-pack-list';

/**
 * Props for {@link DetailsTabPanel}.
 */
export interface DetailsTabPanelProps {
  /** UUID of the currently visible variant pack; excluded from the sibling list. */
  currentVariantPackId: string;
  /** Combined panel data from {@link useItemDetailContentLoader}. */
  data: ItemDetailContentData;
  /** When true, the per-row quantity input on sibling packs is disabled. */
  isQuantityDisabled?: boolean;
  /**
   * Called when a related variant pack row is pressed; the parent pushes the
   * pack onto the panel's drill-down stack.
   */
  onSelectVariantPack: (variantPackId: string) => void;
  /** Mutator that persists the cart on quantity-input blur. */
  saveCart: () => Promise<unknown>;
}

/**
 * Details tab body. Composes a vertical stack of cards: product description
 * (with show-more when the text overflows), product specs, and the related
 * variant packs list. Each section returns `null` when it has no data.
 *
 * @param props - {@link DetailsTabPanelProps}.
 * @returns The rendered tab body.
 */
export const DetailsTabPanel: React.FC<DetailsTabPanelProps> = ({
  currentVariantPackId,
  data,
  isQuantityDisabled,
  onSelectVariantPack,
  saveCart,
}) => {
  const [styles] = useStyles();

  const description =
    data.saltInfo?.description?.trim() ||
    data.itemInfo.description?.trim() ||
    null;

  const relatedVariantPacks = useMemo(
    () =>
      data.relatedVariantPacks.filter(
        (vp) => vp.variantPackId !== currentVariantPackId,
      ),
    [data.relatedVariantPacks, currentVariantPackId],
  );

  return (
    <ScrollView
      contentContainerStyle={styles.bodyContent}
      style={styles.bodyScroll}
    >
      <ProductDescription description={description} />
      <ItemDetailSection
        content={{ kind: 'rows', rows: data.productSpecRows }}
        title={i18n.t(
          'sales_portal.order_entry.item_detail.product_specs.title',
        )}
      />
      <VariantPackList
        isQuantityDisabled={isQuantityDisabled}
        onSelectVariantPack={onSelectVariantPack}
        saveCart={saveCart}
        variantPacks={relatedVariantPacks}
      />
    </ScrollView>
  );
};

const useStyles = createStyles((theme) => ({
  bodyContent: {
    flexDirection: 'column',
    flexGrow: 1,
    gap: theme.sk.spacing.spaceMedium,
  },
  bodyScroll: {
    flex: 1,
  },
}));
